<template>
  <div class="events-view min-h-screen pb-16">
    <div class="max-w-4xl mx-auto pt-8 px-4">
      <div class="d-flex align-center justify-space-between mb-8">
        <div>
          <h1 class="text-h4 font-weight-black text-white mb-2">Events</h1>
          <p class="text-grey-lighten-1">Discover what's happening in the community.</p>
        </div>
        <v-btn
          v-if="user"
          color="primary"
          rounded="pill"
          prepend-icon="mdi-plus"
          class="font-weight-bold px-6"
          @click="showCreateDialog = true"
        >
          Create Event
        </v-btn>
      </div>
      
      <div class="d-flex align-center justify-space-between" style="margin-bottom: 40px !important;">
        <v-tabs
          v-model="viewMode"
          bg-color="transparent"
          color="primary"
          density="compact"
          class="border-bottom border-white-05"
        >
          <v-tab value="list" class="text-none font-weight-bold">
            <v-icon start icon="mdi-view-list"></v-icon>
            List View
          </v-tab>
          <v-tab value="calendar" class="text-none font-weight-bold">
            <v-icon start icon="mdi-calendar-month"></v-icon>
            Calendar View
          </v-tab>
        </v-tabs>
      </div>

      <v-fade-transition mode="out-in">
        <div v-if="loading" class="d-flex justify-center py-12">
          <v-progress-circular indeterminate color="primary"></v-progress-circular>
        </div>
        <div v-else-if="groupedEvents.length === 0" class="text-center py-12 bg-surface-variant-subtle rounded-xl border border-white-05">
          <v-icon icon="mdi-calendar-blank" size="64" class="mb-4 text-grey opacity-20"></v-icon>
          <div class="text-h6 text-grey">No upcoming events found</div>
          <p class="text-caption text-grey opacity-60">Try creating one with #gdievent hashtag!</p>
        </div>
        <div v-else-if="viewMode === 'list' && groupedEvents.length > 0" class="schedule-list">
          <template v-for="(group, index) in groupedEvents" :key="group.date">
            <!-- Past Events Divider -->
            <div 
              v-if="group.isPast && (index === 0 || !groupedEvents[index-1].isPast)" 
              class="past-events-divider d-flex align-center"
              style="margin: 80px 0 !important;"
            >
              <v-divider class="border-white-05 opacity-20"></v-divider>
              <div class="px-10 d-flex align-center bg-surface-variant-subtle rounded-pill py-2 border border-white-05 mx-4" style="white-space: nowrap;">
                <v-icon icon="mdi-history" size="18" class="mr-2 text-grey"></v-icon>
                <span class="font-weight-black text-grey text-uppercase letter-spacing-1" style="font-size: 0.65rem; line-height: 1;">Past Events</span>
              </div>
              <v-divider class="border-white-05 opacity-20"></v-divider>
            </div>

            <div class="date-group mb-8" :class="{ 'opacity-60': group.isPast }">
            <div class="date-header d-flex align-center mb-4 scale-in">
              <div class="date-pill bg-primary px-4 py-1 rounded-pill text-caption font-weight-black mr-4">
                {{ formatHeaderDate(group.date) }}
              </div>
              <v-divider class="border-white-05 opacity-20"></v-divider>
            </div>

            <div class="events-container">
              <div 
                v-for="event in group.events" 
                :key="event.uri" 
                class="event-card mb-4 bg-surface border border-white-05 rounded-xl overflow-hidden hover-rise cursor-pointer"
                @click="openEvent(event)"
              >
                <div class="d-flex">
                  <!-- Time and Place Info (Left) -->
                  <div class="pa-6 border-right border-white-05 text-center d-flex flex-column justify-center" style="min-width: 140px;">
                    <div v-if="event.time" class="text-subtitle-1 font-weight-black text-primary mb-1">{{ event.time }}</div>
                    <div v-else class="text-subtitle-2 text-grey-darken-1 mb-1">TBA</div>
                    <div class="text-caption text-grey d-flex align-center justify-center">
                      <v-icon icon="mdi-map-marker-outline" size="14" class="mr-1"></v-icon>
                      <span class="text-truncate" style="max-width: 100px;">{{ event.place || 'Online' }}</span>
                    </div>
                  </div>

                  <!-- Details (Center) -->
                  <div class="pa-6 flex-grow-1">
                    <div class="d-flex align-center mb-2 gap-2">
                       <h3 class="text-h6 font-weight-bold text-white">{{ event.name }}</h3>
                       <v-chip
                         v-if="isEventRunning(event)"
                         color="success"
                         size="x-small"
                         variant="flat"
                         class="font-weight-black pulse-animation"
                       >
                         RUNNING
                       </v-chip>
                    </div>
                    
                    <div v-if="event.endDate" class="text-caption text-primary font-weight-bold mb-2">
                       <v-icon icon="mdi-calendar-range" size="14" class="mr-1"></v-icon>
                       {{ formatHeaderDate(event.date) }} - {{ formatHeaderDate(event.endDate) }}
                       <span v-if="event.endTime" class="ml-1 opacity-80">(Ends at {{ event.endTime }})</span>
                    </div>
                    <p class="text-body-2 text-grey-lighten-1 line-clamp-2 mb-4">{{ event.description }}</p>
                    
                    <div class="d-flex align-center">
                      <v-avatar size="24" class="mr-2 border border-white-10">
                        <v-img v-if="event.author.avatar" :src="event.author.avatar"></v-img>
                        <v-icon v-else icon="mdi-account" size="14"></v-icon>
                      </v-avatar>
                      <span class="text-caption text-grey">by @{{ event.author.handle }}</span>
                    </div>
                  </div>

                  <!-- Image Preview (Right) -->
                  <div v-if="event.image" class="pa-2 d-none d-sm-block">
                    <v-img
                      :src="event.image"
                      width="120"
                      height="120"
                      cover
                      class="rounded-lg border border-white-05"
                    ></v-img>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </template>
      </div>

      <!-- Load More Button -->
      <div v-if="cursor != null && cursor !== '' && !loading && viewMode === 'list'" class="d-flex justify-center py-10">
        <v-btn
          color="primary"
          variant="tonal"
          rounded="pill"
          size="large"
          class="px-10 font-weight-black"
          :loading="loadingMore"
          @click="loadMoreEvents"
        >
          <v-icon start icon="mdi-chevron-down"></v-icon>
          Load More Events
        </v-btn>
      </div>

      <!-- Calendar View -->
      <div v-else-if="viewMode === 'calendar'" class="calendar-view-container animate-fade-in">
        <EventCalendar 
          :events="events" 
          @open-event="openEvent"
        />
      </div>
    </v-fade-transition>
    </div>

    <!-- Create Event Dialog -->
    <v-dialog v-model="showCreateDialog" max-width="900px" persistent scrollable>
      <!-- Mention Suggestions Dropdown -->
      <MentionSuggestions
        v-if="mentionState.visible"
        :query="mentionState.query"
        :position="{ top: mentionState.top, left: mentionState.left }"
        @select="insertMention"
        @close="mentionState.visible = false"
      />

      <v-card class="rounded-xl bg-surface border border-white-05 overflow-hidden">
        <v-card-title class="pa-6 pb-2 d-flex align-center justify-space-between">
          <span class="text-h5 font-weight-black text-white">Post an Event</span>
          <v-btn v-if="!publishedPostUri" icon="mdi-close" variant="text" @click="showCreateDialog = false"></v-btn>
        </v-card-title>

        <v-card-text class="pa-0 d-flex flex-column overflow-hidden" style="height: 70vh;">
          <div v-if="publishedPostUri" class="pa-12 text-center animate-fade-in">
            <v-icon icon="mdi-check-circle" color="success" size="64" class="mb-4"></v-icon>
            <h3 class="text-h5 font-weight-black text-white mb-2">Event Published!</h3>
            <p class="text-body-2 text-grey-lighten-1 mb-6">
              Your event post has been sent to Bluesky. 
              <br>
              <span class="text-primary font-weight-bold">Note:</span> It might take a few minutes for the event board to sync and display your new event.
            </p>
            <v-btn
              color="primary"
              variant="elevated"
              rounded="pill"
              class="px-8 font-weight-black mb-4"
              @click="viewPublishedThread"
            >
              View Post Thread
            </v-btn>
          </div>

          <v-row v-else no-gutters class="flex-grow-1 overflow-hidden" style="height: 0;">
            <!-- Form Column -->
            <v-col cols="12" md="7" class="pa-6 border-right d-flex flex-column overflow-hidden h-100">
              <div v-if="!publishedPostUri" class="d-flex align-center justify-space-between mb-4 mt-2">
                <span class="text-subtitle-2 font-weight-black text-grey-lighten-1 text-uppercase letter-spacing-1">Event Details</span>
                <v-btn
                  color="primary"
                  variant="flat"
                  size="small"
                  rounded="pill"
                  prepend-icon="mdi-auto-fix"
                  class="text-none font-weight-bold magic-pulsate"
                  @click="showMagicDialog = true"
                >
                  Magic Paste
                </v-btn>
              </div>
              <v-form ref="formRef" @submit.prevent="publishEvent" class="flex-grow-1 overflow-y-auto pr-2" style="min-height: 0;">
                <v-text-field
                  v-model="newEvent.name"
                  label="Event Name"
                  variant="outlined"
                  density="comfortable"
                  class="mb-4"
                  required
                ></v-text-field>

                <v-row>
                  <v-col cols="6">
                    <v-text-field
                      v-model="newEvent.date"
                      label="Start Date"
                      type="date"
                      variant="outlined"
                      density="comfortable"
                      required
                    ></v-text-field>
                  </v-col>
                  <v-col cols="6">
                    <v-text-field
                      v-model="newEvent.time"
                      label="Start Time"
                      placeholder="e.g. 19:00 WIB"
                      variant="outlined"
                      density="comfortable"
                    ></v-text-field>
                  </v-col>
                </v-row>

                <div class="d-flex align-center justify-space-between mb-1 mt-4">
                   <div class="text-caption font-weight-bold text-grey">Duration (Optional)</div>
                   <v-btn
                     v-if="newEvent.endDate || newEvent.endTime"
                     variant="text"
                     color="error"
                     size="x-small"
                     class="text-none font-weight-bold"
                     @click="resetDuration"
                   >
                     Reset Duration
                   </v-btn>
                </div>
                <v-row class="mb-4">
                  <v-col cols="6">
                    <v-text-field
                      v-model="newEvent.endDate"
                      label="End Date"
                      type="date"
                      variant="outlined"
                      density="comfortable"
                      hide-details
                    ></v-text-field>
                  </v-col>
                  <v-col cols="6">
                    <v-text-field
                      v-model="newEvent.endTime"
                      label="End Time"
                      placeholder="e.g. 21:00 WIB"
                      variant="outlined"
                      density="comfortable"
                      hide-details
                    ></v-text-field>
                  </v-col>
                </v-row>

                <v-text-field
                  v-model="newEvent.place"
                  label="Place / Location"
                  placeholder="e.g. Jakarta or Online"
                  variant="outlined"
                  density="comfortable"
                  class="mt-4 mb-4"
                ></v-text-field>

                <v-textarea
                  v-model="newEvent.description"
                  label="Additional Information"
                  variant="outlined"
                  density="comfortable"
                  rows="3"
                  hide-details
                  @input="handleMentionInput"
                ></v-textarea>

                <div class="d-flex align-center justify-end py-3 px-1">
                  <v-progress-circular
                    :model-value="progressValue"
                    :color="progressColor"
                    size="24"
                    width="3"
                    class="mr-3"
                  >
                    <template v-slot:default v-if="charCount > 280">
                      <span class="text-tiny" style="font-size: 0.6rem">{{ 300 - charCount }}</span>
                    </template>
                  </v-progress-circular>
                  <span 
                    class="text-caption font-weight-bold"
                    :class="isOverLimit ? 'text-error' : 'text-grey'"
                  >
                    {{ charCount }} / 300
                  </span>
                </div>

                <div class="mb-4">
                  <input
                    type="file"
                    ref="fileInput"
                    class="d-none"
                    accept="image/*"
                    multiple
                    @change="onFileSelected"
                  />
                  <v-btn
                    prepend-icon="mdi-image-plus"
                    variant="outlined"
                    color="primary"
                    rounded="pill"
                    class="text-none font-weight-bold"
                    @click="fileInput?.click()"
                    :disabled="publishing || mediaPreviews.length >= 4"
                  >
                    Add Images ({{ mediaPreviews.length }}/4)
                  </v-btn>

                  <div v-if="mediaPreviews.length > 0" class="d-flex flex-wrap gap-2 mt-4">
                    <div v-for="(preview, index) in mediaPreviews" :key="index" class="position-relative">
                      <v-img
                        :src="preview"
                        width="80"
                        height="80"
                        cover
                        class="rounded-lg border border-white-05"
                      ></v-img>
                      <v-btn
                        icon="mdi-close"
                        size="x-small"
                        color="black"
                        class="remove-media-btn"
                        @click="removeMedia(index)"
                        :disabled="publishing"
                      ></v-btn>
                    </div>
                  </div>
                </div>
              </v-form>
            </v-col>

            <!-- Preview Column -->
            <v-col cols="12" md="5" class="pa-6 bg-black-20 d-none d-md-flex flex-column border-left border-white-05 overflow-hidden h-100">
              <div class="text-subtitle-2 font-weight-black text-grey-lighten-1 mb-4 text-uppercase letter-spacing-1">Post Preview</div>
              
              <div class="flex-grow-1 overflow-y-auto mb-4 pr-1 scroll-container" style="min-height: 0;">
                <div class="preview-card pa-4 rounded-xl border border-white-05 bg-black-05">
                  <div class="d-flex mb-3">
                    <v-avatar size="40" class="mr-3 border border-white-10">
                      <v-img v-if="user?.handle && userProfile?.avatar" :src="userProfile.avatar"></v-img>
                      <v-icon v-else icon="mdi-account"></v-icon>
                    </v-avatar>
                    <div class="min-width-0">
                      <div class="text-subtitle-2 font-weight-black text-white text-truncate">{{ userProfile?.displayName || user?.handle || 'You' }}</div>
                      <div class="text-caption text-grey text-truncate">@{{ user?.handle || 'handle' }}</div>
                    </div>
                  </div>

                  <div 
                    class="preview-content text-body-2 text-white white-space-pre-wrap"
                    style="word-break: break-word; line-height: 1.5;"
                    v-html="renderRichText(formattedPostText)"
                    @click="handleRichTextClick($event, { router })"
                  ></div>

                  <!-- Media Preview in Content -->
                  <v-row v-if="mediaPreviews.length > 0" class="mt-3 g-1" dense>
                    <v-col v-for="(preview, i) in mediaPreviews" :key="i" :cols="mediaPreviews.length > 1 ? 6 : 12">
                       <v-img :src="preview" class="rounded-lg shadow-sm" cover height="120" v-if="mediaPreviews.length > 1"></v-img>
                       <v-img :src="preview" class="rounded-lg shadow-sm" cover height="240" v-else></v-img>
                    </v-col>
                  </v-row>
                </div>
              </div>

              <div style="margin-top: 10px !important;">
                <v-alert
                  variant="tonal"
                  color="primary"
                  density="compact"
                  icon="mdi-information-outline"
                  class="text-caption rounded-lg"                
                >
                  This is exactly how your event will appear on the feed.
                </v-alert>
              </div>
            </v-col>
          </v-row>
        </v-card-text>

        <v-card-actions class="pa-6 pt-0" v-if="!publishedPostUri">
          <v-spacer></v-spacer>
          <v-btn variant="text" class="rounded-pill px-6" @click="showCreateDialog = false">Cancel</v-btn>
          <v-btn 
            color="primary" 
            variant="flat" 
            class="rounded-pill px-8 font-weight-black"
            :loading="publishing"
            @click="publishEvent"
            :disabled="!isFormValid"
          >
            Publish
          </v-btn>
        </v-card-actions>
        <v-card-actions class="pa-6 pt-0" v-else>
          <!-- Close button removed to force viewing thread per user request -->
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Magic Paste Dialog -->
    <v-dialog v-model="showMagicDialog" max-width="500px">
      <v-card class="rounded-xl bg-surface border border-white-05">
        <v-card-title class="pa-6 pb-0 font-weight-black">Magic Paste 🪄</v-card-title>
        <v-card-text class="pa-6">
          <p class="text-body-2 text-grey-lighten-1 mb-4">Paste your event invitation text here and I'll try to fill the form for you!</p>
          <v-textarea
            v-model="magicText"
            placeholder="e.g. Acara Kumkum GameLan di adakan pada: 📅 Tanggal : 6 Maret 2026..."
            variant="outlined"
            rows="8"
            hide-details
            class="mb-4"
          ></v-textarea>
        </v-card-text>
        <v-card-actions class="pa-6 pt-0">
          <v-spacer></v-spacer>
          <v-btn variant="text" @click="showMagicDialog = false">Cancel</v-btn>
          <v-btn color="primary" variant="flat" rounded="pill" class="px-6 font-weight-bold" @click="handleMagicParse">Parse Text</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Global Snackbar for notifications -->
    <v-snackbar v-model="snackbar.show" :color="snackbar.color" rounded="pill">
      {{ snackbar.text }}
    </v-snackbar>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, computed, watch } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { searchEventPosts, createPost, uploadBlob, currentUser, type Event, agent } from '@/services/bluesky';
import { compressImage } from '@/utils/image';
import { renderRichText, handleRichTextClick } from '@/utils/richtext';
import { viewThreadByUri } from '@/utils/navigation';
import MentionSuggestions from '@/components/MentionSuggestions.vue';
import EventCalendar from '@/components/EventCalendar.vue';

const router = useRouter();
const route = useRoute();
const user = currentUser;

const loading = ref(true);
const cursor = ref<string | undefined>(undefined);
const loadingMore = ref(false);
const viewMode = ref<'list' | 'calendar'>('list');
const events = ref<Event[]>([]);
const userProfile = ref<any>(null);
const publishing = ref(false);
const showCreateDialog = ref(false);
const showMagicDialog = ref(false);
const magicText = ref('');
const fileInput = ref<HTMLInputElement | null>(null);

const mentionState = ref({
  visible: false,
  query: '',
  top: 0,
  left: 0,
  cursorPos: 0
});

const handleMentionInput = (e: any) => {
  const el = e.target;
  const value = el.value;
  const cursorPos = el.selectionStart;
  
  const textBefore = value.substring(0, cursorPos);
  const lastAtMatch = textBefore.match(/(?:^|\s)@([a-zA-Z0-9.-]*)$/);
  
  if (lastAtMatch) {
    const rect = el.getBoundingClientRect();
    mentionState.value = {
      visible: true,
      query: lastAtMatch[1],
      top: rect.top + 40,
      left: rect.left + 20,
      cursorPos: cursorPos
    };
  } else {
    mentionState.value.visible = false;
  }
};

const insertMention = (mentionUser: any) => {
  const textBefore = newEvent.value.description.substring(0, mentionState.value.cursorPos);
  const lastAtPos = textBefore.lastIndexOf('@');
  const beforeAt = textBefore.substring(0, lastAtPos);
  const textAfter = newEvent.value.description.substring(mentionState.value.cursorPos);
  
  newEvent.value.description = beforeAt + `@${mentionUser.handle} ` + textAfter;
  mentionState.value.visible = false;
};

const mediaFiles = ref<File[]>([]);
const mediaPreviews = ref<string[]>([]);

const newEvent = ref({
  name: '',
  date: new Date().toISOString().split('T')[0],
  time: '',
  endDate: '',
  endTime: '',
  place: '',
  description: ''
});

const publishedPostUri = ref<string | null>(null);

const handleMagicParse = () => {
  const text = magicText.value;
  if (!text) return;

  // Extract Name (first meaningful line or line containing 'Kumkum' or 'Acara')
  const lines = text.split('\n').map(l => l.trim()).filter(l => l.length > 0);
  let extractedName = '';
  const nameLine = lines.find(l => l.includes('Kumkum') || l.toLowerCase().includes('acara'));
  if (nameLine) {
    extractedName = nameLine.replace(/Acara|di adakan pada[ \t]*[:]|📅|⏰|📍|[:]/gi, '').trim();
  } else if (lines.length > 1) {
    extractedName = lines[1]; // Often second line is the title
  }
  if (extractedName) newEvent.value.name = extractedName;

  // Extract Date
  const dateRegex = /(\d{1,2})\s+(Januari|Februari|Maret|April|Mei|Juni|Juli|Agustus|September|Oktober|November|Desember|Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)\s+(\d{4})/i;
  const dateMatch = text.match(dateRegex);
  if (dateMatch) {
    const day = dateMatch[1].padStart(2, '0');
    const monthStr = dateMatch[2].toLowerCase();
    const year = dateMatch[3];
    
    const monthMap: { [key: string]: string } = {
      'januari': '01', 'jan': '01', 'februari': '02', 'feb': '02',
      'maret': '03', 'mar': '03', 'april': '04', 'apr': '04',
      'mei': '05', 'may': '05', 'juni': '06', 'jun': '06',
      'juli': '07', 'jul': '07', 'agustus': '08', 'agu': '08', 'aug': '08',
      'september': '09', 'sep': '09', 'oktober': '10', 'okt': '10', 'oct': '10',
      'november': '11', 'nov': '11', 'desember': '12', 'des': '12', 'dec': '12'
    };
    
    const month = monthMap[monthStr] || '01';
    newEvent.value.date = `${year}-${month}-${day}`;
  }

  // Extract Time
  const timeRegex = /(?:Pukul|Jam|Time|⏰)[ \t]*[:]*[ \t]*([^\n]*)/i;
  const timeMatch = text.match(timeRegex);
  if (timeMatch) {
    newEvent.value.time = timeMatch[1].trim();
  }

  // Extract Place
  const placeRegex = /(?:Lokasi|Place|Venue|📍)[ \t]*[:]*[ \t]*([^\n]*)/i;
  const placeMatch = text.match(placeRegex);
  if (placeMatch) {
    newEvent.value.place = placeMatch[1].trim();
  }

  // Description (rest of text or specific clean up)
  newEvent.value.description = text.substring(0, 200) + (text.length > 200 ? '...' : '');
  
  showMagicDialog.value = false;
  magicText.value = '';
  snackbar.value = { show: true, text: 'Magic Parse successful!', color: 'success' };
};

const snackbar = ref({
  show: false,
  text: '',
  color: 'success'
});

const formattedPostText = computed(() => {
  let text = `📢 ${newEvent.value.name}\n🗓️ ${newEvent.value.date}`;
  if (newEvent.value.time) text += ` ${newEvent.value.time}`;
  
  if (newEvent.value.endDate) {
    text += `\n⏳ ${newEvent.value.endDate}`;
    if (newEvent.value.endTime) text += ` ${newEvent.value.endTime}`;
  }
  
  text += `\n📍 ${newEvent.value.place}\n\n${newEvent.value.description}\n\n#gdievent`;
  return text;
});

const charCount = computed(() => formattedPostText.value.length);
const isOverLimit = computed(() => charCount.value > 300);

const isFormValid = computed(() => {
  return !!newEvent.value.name && !!newEvent.value.date && !isOverLimit.value;
});

const progressValue = computed(() => {
  return Math.min((charCount.value / 300) * 100, 100);
});

const progressColor = computed(() => {
  if (charCount.value > 300) return 'error';
  if (charCount.value > 250) return 'warning';
  return 'primary';
});

const isEventRunning = (event: Event) => {
  const todayStr = new Date().toISOString().split('T')[0];
  if (event.endDate) {
    return event.date <= todayStr && todayStr <= event.endDate;
  }
  return event.date === todayStr;
};

const resetDuration = () => {
  newEvent.value.endDate = '';
  newEvent.value.endTime = '';
};

const fetchEvents = async () => {
  loading.value = true;
  cursor.value = undefined;
  try {
    const res = await searchEventPosts();
    // Sort by date ascending
    events.value = res.events.sort((a, b) => a.date.localeCompare(b.date));
    cursor.value = res.cursor;
  } catch (e) {
    console.error('Failed to fetch events:', e);
  } finally {
    loading.value = false;
  }
};

const loadMoreEvents = async () => {
  if (loadingMore.value || !cursor.value) return;
  loadingMore.value = true;
  try {
    const res = await searchEventPosts(cursor.value);
    const newEvents = res.events;
    events.value = [...events.value, ...newEvents].sort((a, b) => a.date.localeCompare(b.date));
    cursor.value = res.cursor;
  } catch (e) {
    console.error('Failed to load more events:', e);
  } finally {
    loadingMore.value = false;
  }
};

const fetchUserProfile = async () => {
  if (user.value?.did) {
    try {
      const profile = await agent.getProfile({ actor: user.value.did });
      userProfile.value = profile.data;
    } catch (e) {
      console.error('Failed to fetch user profile:', e);
    }
  }
};

const groupedEvents = computed(() => {
  const groups: { date: string, events: Event[], isPast: boolean }[] = [];
  const todayStr = new Date().toISOString().split('T')[0];
  
  events.value.forEach(event => {
    // If it has an end date, use that to determine if it's past
    const effectiveEndDate = event.endDate || event.date;
    const isPast = effectiveEndDate < todayStr;
    
    const existingGroup = groups.find(g => g.date === event.date);
    if (existingGroup) {
      existingGroup.events.push(event);
    } else {
      groups.push({ 
        date: event.date, 
        events: [event],
        isPast: isPast
      });
    }
  });
  
  // Sort: Future/Running events first (ascending by date), then Past events (descending by date)
  const future = groups.filter(g => !g.isPast).sort((a, b) => a.date.localeCompare(b.date));
  const past = groups.filter(g => g.isPast).sort((a, b) => b.date.localeCompare(a.date));
  
  return [...future, ...past];
});

const formatHeaderDate = (dateStr: string) => {
  try {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { 
      weekday: 'short', 
      month: 'short', 
      day: 'numeric',
      year: 'numeric' 
    }).toUpperCase();
  } catch (e) {
    return dateStr;
  }
};

const openEvent = (event: Event) => {
  const parts = event.uri.split('/');
  const handle = event.author.handle || event.author.did;
  const rkey = parts[parts.length - 1];
  router.push(`/profile/${handle}/post/${rkey}`);
};

const viewPublishedThread = () => {
  if (!publishedPostUri.value) return;
  const parts = publishedPostUri.value.split('/');
  const handle = user.value?.handle || user.value?.did;
  const rkey = parts[parts.length - 1];
  router.push(`/profile/${handle}/post/${rkey}`);
  showCreateDialog.value = false;
  setTimeout(closeAndReset, 500);
};

const closeAndReset = () => {
  showCreateDialog.value = false;
  setTimeout(() => {
    publishedPostUri.value = null;
    newEvent.value = {
      name: '',
      date: new Date().toISOString().split('T')[0],
      time: '',
      endDate: '',
      endTime: '',
      place: '',
      description: ''
    };
    mediaFiles.value = [];
    mediaPreviews.value = [];
  }, 300);
};

const onFileSelected = (event: any) => {
  const files = event.target.files;
  if (!files) return;
  
  Array.from(files).forEach((file: any) => {
    if (mediaFiles.value.length < 4 && file.type.startsWith('image/')) {
      mediaFiles.value.push(file as File);
      const reader = new FileReader();
      reader.onload = (e) => {
        mediaPreviews.value.push(e.target?.result as string);
      };
      reader.readAsDataURL(file as File);
    }
  });
  if (fileInput.value) fileInput.value.value = '';
};

const removeMedia = (index: number) => {
  mediaFiles.value.splice(index, 1);
  mediaPreviews.value.splice(index, 1);
};

const publishEvent = async () => {
  if (!newEvent.value.name || !newEvent.value.date) {
    snackbar.value = { show: true, text: 'Please fill name and date', color: 'error' };
    return;
  }

  publishing.value = true;
  try {
    let embed = undefined;
    
    // Handle Images
    if (mediaFiles.value.length > 0) {
      const blobs = await Promise.all(
        mediaFiles.value.map(async (file: File) => {
          const compressed = await compressImage(file);
          return uploadBlob(compressed, 'image/jpeg');
        })
      );
      
      embed = {
        $type: 'app.bsky.embed.images',
        images: blobs.map((blob, i) => ({
          image: blob,
          alt: `${newEvent.value.name} - Image ${i + 1}`
        }))
      };
    }

    const postText = formattedPostText.value;
    

    const response = await createPost(postText, embed);
    
    publishedPostUri.value = response.uri;
    snackbar.value = { show: true, text: 'Event published successfully!', color: 'success' };
    
    // Automatically redirect after a short delay
    setTimeout(() => {
      if (response.uri) {
        viewThreadByUri(response.uri, router, user.value?.handle);
        showCreateDialog.value = false;
        setTimeout(closeAndReset, 500);
      }
    }, 1500);

    // Refresh list
    await fetchEvents();
  } catch (e) {
    console.error('Failed to publish event:', e);
    snackbar.value = { show: true, text: 'Error publishing event', color: 'error' };
  } finally {
    publishing.value = false;
  }
};

onMounted(() => {
  fetchEvents();
  fetchUserProfile();
});

watch(() => route.fullPath, () => {
  fetchEvents();
});
</script>

<style scoped>
.events-view {
  background: radial-gradient(circle at top right, rgba(219, 168, 88, 0.05), transparent 40%),
              radial-gradient(circle at bottom left, rgba(219, 168, 88, 0.02), transparent 40%);
}

.date-pill {
  letter-spacing: 1px;
  box-shadow: 0 4px 12px rgba(219, 168, 88, 0.3);
  white-space: nowrap;
}

.event-card {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  background: rgba(255, 255, 255, 0.02) !important;
}

.event-card:hover {
  background: rgba(255, 255, 255, 0.05) !important;
  transform: translateY(-2px);
  border-color: rgba(219, 168, 88, 0.3) !important;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.2);
}

.border-right {
  border-right: 1px solid rgba(255, 255, 255, 0.08);
}

.scale-in {
  animation: scaleIn 0.4s ease-out forwards;
}

@keyframes scaleIn {
  from { opacity: 0; transform: scale(0.95); }
  to { opacity: 1; transform: scale(1); }
}

.line-clamp-2 {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.hover-rise:hover {
  transform: translateY(-4px);
}

.remove-media-btn {
  position: absolute;
  top: -8px;
  right: -8px;
  background: rgba(0, 0, 0, 0.8) !important;
  color: white !important;
  z-index: 2;
}

.gap-2 {
  gap: 8px;
}

.white-space-pre-wrap {
  white-space: pre-wrap !important;
}

.letter-spacing-1 {
  letter-spacing: 1px !important;
}

.bg-black-20 {
  background-color: rgba(0, 0, 0, 0.2) !important;
}

.bg-black-05 {
  background-color: rgba(0, 0, 0, 0.05) !important;
}

.preview-card {
  transition: all 0.2s ease;
}

.text-tiny {
  font-size: 0.65rem;
  line-height: 1;
}

.magic-pulsate {
  animation: magic-glow 3s infinite;
  box-shadow: 0 0 0 0 rgba(219, 168, 88, 0.4);
}

@keyframes magic-glow {
  0% {
    box-shadow: 0 0 0 0 rgba(219, 168, 88, 0.4);
  }
  70% {
    box-shadow: 0 0 0 10px rgba(219, 168, 88, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(219, 168, 88, 0);
  }
}

.pulse-animation {
  animation: pulse 2s infinite;
  box-shadow: 0 0 0 0 rgba(var(--v-theme-success), 0.7);
}

@keyframes pulse {
  0% {
    box-shadow: 0 0 0 0 rgba(var(--v-theme-success), 0.7);
  }
  70% {
    box-shadow: 0 0 0 10px rgba(var(--v-theme-success), 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(var(--v-theme-success), 0);
  }
}
</style>
